import {GenerateSignedReplayParamsResponse} from 'app_api/projects/replays/types';

export type RecordingMetadata = {
  replayId: string;
  time: number;
  distinctId: string;
  name: string;
  avatarUrl: string;
  email: string;
  duration: number;
  replayEnv: string;
};

export type RecordingEventMetadata = {
  eventName: string;
  eventTime: number;
  quantity: number;
};

export type RecordingEvents = {
  replayId: string;
  replayEvents: RecordingEventMetadata[];
};

export type RecordingInactivePeriod = {
  startTime: number;
  endTime: number;
};

export enum RecordingPlayerTrackingEvent {
  Watch = `[Recording Player] Replay Watched`,
  Finish = `[Recording Player] Replay Finished`,
  Close = `[Recording Player] Modal Closed`,
  Open = `[Recording Player] Modal Opened`,
  Feedback = `[Recording Player] Feedback Submitted`,
  ListLoaded = `[Recording Player] Replay List Loaded`,
  ListLoadFailed = `[Recording Player] Replay List Load Failed`,
}

export enum RecordingPlayerSource {
  UsersProfile = `User Profile`,
  UsersProfileLink = `User Profile Link`,
  Funnels = `Funnels`,
  Event = `Event`,
  Cohort = `Cohort`,
  InsightsProfile = `InsightsProfile`,
  Home = `Home`,
}

export enum RecordingSpeed {
  Half = 0.5,
  ThreeQuarters = 0.75,
  Normal = 1,
  Double = 2,
  Quadruple = 4,
  Octuple = 8,
  Dectuple = 10,
}

export enum TimeDisplaySetting {
  Hours = `hours`,
  Minutes = `minutes`,
  Seconds = `seconds`,
}

export enum WindowMessageRecipient {
  ParentApp,
  ChildReplay,
}

export type SignedReplayParams = GenerateSignedReplayParamsResponse['results'];

export type ParentAppMessagePayload = {
  embedReady?: true;
  embedBlocked?: true;
  error?: string;
  loadingRecording?: boolean;
  playerClicked?: true;
  recordingDuration?: number;
  recordingFinished?: boolean;
  recordingInactivePeriods?: RecordingInactivePeriod[];
  recordingProgress?: number;
  recordingSkippingInactive?: boolean;
  recordingSpeed?: RecordingSpeed;
  recordingStarted?: boolean;
};

export type LoadReplayParams = {
  // authenticated params for the embedded player to be able to pull replay files
  signedReplayParams: SignedReplayParams;
  // target origin that the embedded player should use to send messages to the parent
  targetOrigin: string;
};

export type EmbedReplayPayload = {
  loadReplayParams?: LoadReplayParams;
  recordingPaused?: boolean;
  recordingProgress?: number;
  recordingSpeed?: RecordingSpeed;
  targetOrigin?: string;
};

export type ParentAppReplayMessage = {
  recipient: WindowMessageRecipient.ParentApp;
  payload: ParentAppMessagePayload;
};

export type EmbedReplayMessage = {
  recipient: WindowMessageRecipient.ChildReplay;
  payload: EmbedReplayPayload;
};

export type ReplayMessage = EmbedReplayMessage | ParentAppReplayMessage;

export function isReplayWindowMessage(event: MessageEvent): event is MessageEvent<ReplayMessage> {
  return event.data?.recipient !== undefined;
}
